import i18next from 'i18next';

/**
 * Known English API / legacy toast strings → common i18n keys.
 * Keeps toasts bilingual when the backend still returns English.
 */
const KNOWN_ENGLISH_MESSAGES = Object.freeze({
    'A record with this value already exists': 'common:messages.duplicateError',
    'A service catalog item with this code already exists': 'itsmServiceCatalog:messages.codeDuplicate',
    'Please check the information you entered': 'common:messages.validationError',
    'Please check the form for errors': 'common:messages.formValidationError',
    'You are not authorized for this action': 'common:messages.authError',
    'You do not have permission for this action': 'common:messages.authError',
    'An unexpected error occurred': 'common:messages.unexpectedError',
    'An error occurred': 'common:messages.error',
    'Record not found': 'common:messages.notFoundError',
    'Failed to create record': 'common:messages.saveError',
    'Validation failed': 'common:messages.validationError',
    'Tenant context required': 'common:messages.tenantRequired',
    'Tenant selection is required': 'common:messages.tenantRequired',
    'Kiracı seçimi gerekli': 'common:messages.tenantRequired',
    'Unsupported entity type': 'lifecycle:messages.unsupportedEntityType',
    'Invalid entity id': 'lifecycle:messages.invalidEntityId',
});

/**
 * Resolve a user-facing toast/error string for Integration layers.
 *
 * @param {unknown} error
 * @param {{
 *   notFoundKey?: string,
 *   notFoundDefault?: string,
 *   t?: typeof i18next.t,
 * }} [options]
 * @returns {string}
 */
export function getIntegrationErrorMessage(error, options = {}) {
    const t = typeof options.t === 'function'
        ? options.t
        : (key, opts) => i18next.t(key, opts);

    if (error?.isValidationError?.()) {
        return t('common:messages.validationError', {
            defaultValue: 'Please check the information you entered',
        });
    }
    if (error?.isAuthError?.()) {
        return t('common:messages.authError', {
            defaultValue: 'You do not have permission for this action',
        });
    }
    if (error?.isNotFound?.()) {
        if (options.notFoundKey) {
            return t(options.notFoundKey, {
                defaultValue: options.notFoundDefault || 'Record not found',
            });
        }
        return t('common:messages.notFoundError', {
            defaultValue: options.notFoundDefault || 'Record not found',
        });
    }

    const raw = (
        typeof error?.message === 'string'
            ? error.message
            : typeof error === 'string'
                ? error
                : ''
    ).trim();
    if (!raw) {
        return t('common:messages.unexpectedError', {
            defaultValue: 'An unexpected error occurred',
        });
    }

    const knownKey = KNOWN_ENGLISH_MESSAGES[raw];
    if (knownKey) {
        return t(knownKey, { defaultValue: raw });
    }

    // "Failed to create record: …" — translate the known prefix when present
    for (const [english, key] of Object.entries(KNOWN_ENGLISH_MESSAGES)) {
        if (raw.startsWith(`${english}:`)) {
            const suffix = raw.slice(english.length + 1).trim();
            const head = t(key, { defaultValue: english });
            return suffix ? `${head}: ${suffix}` : head;
        }
    }

    // Raw i18n key leaked into toast
    if (/^[a-zA-Z][\w-]*:[a-zA-Z][\w.-]*$/.test(raw)) {
        const translated = t(raw, { defaultValue: '' });
        if (translated && translated !== raw) return translated;
    }

    return raw;
}
